import { format, parseISO } from "date-fns";

/**
 * Converts a local date to a date-only string that maintains consistency across timezones
 * This preserves the date as it was selected, regardless of timezone
 * @param date - The date object selected by the user
 * @returns Date string in YYYY-MM-DD format that maintains the date
 */
export function convertLocalDateToConsistentString(date: Date): string {
  // Extract just the date components (year, month, day) without time/timezone
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  
  return `${year}-${month}-${day}`;
}

/**
 * Formats a date string for display (handles both old ISO strings and new date strings)
 * @param dateString - The date string (either YYYY-MM-DD or ISO string)
 * @param formatString - The format string for date-fns
 * @returns Formatted date string
 */
export function formatConsistentDate(
  dateString: string | null,
  formatString: string = "EEE, d MMM yyyy"
): string {
  if (!dateString) return "";
  
  try {
    let date: Date;
    
    // Check if it's an ISO string (contains 'T' or 'Z')
    if (dateString.includes('T') || dateString.includes('Z')) {
      // Old format: ISO string - extract just the date part
      const datePart = dateString.split('T')[0];
      date = new Date(datePart + 'T00:00:00');
    } else if (dateString.includes('GMT') || dateString.includes('UTC')) {
      // Handle full JavaScript Date string format (e.g., "Sun May 24 2026 05:30:00 GMT+0530 (India Standard Time)")
      // Parse the full date string and extract just the date components
      const parsedDate = new Date(dateString);
      if (!isNaN(parsedDate.getTime())) {
        // Extract date components to avoid timezone issues
        const year = parsedDate.getFullYear();
        const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
        const day = String(parsedDate.getDate()).padStart(2, '0');
        date = new Date(`${year}-${month}-${day}T00:00:00`);
      } else {
        throw new Error('Invalid full date string');
      }
    } else if (dateString.includes(' ')) {
      // Handle date strings with time (e.g., "2026-07-18 21:00") - extract just the date part
      const datePart = dateString.split(' ')[0];
      date = new Date(datePart + 'T00:00:00');
    } else {
      // New format: YYYY-MM-DD string
      date = new Date(dateString + 'T00:00:00');
    }
    
    // Validate that the date is valid before formatting
    if (isNaN(date.getTime())) {
      console.warn("Invalid date for formatting:", dateString);
      return "Invalid date";
    }
    
    return format(date, formatString);
  } catch (error) {
    console.warn("Error formatting consistent date:", error, "Input:", dateString);
    return "Invalid date";
  }
}

/**
 * Creates a date object from a date string for calendar display (handles both old and new formats)
 * @param dateString - The date string (either YYYY-MM-DD or ISO string)
 * @returns Date object for calendar display
 */
export function createDateForCalendar(dateString: string | null): Date | undefined {
  if (!dateString) return undefined;
  
  try {
    let date: Date;
    
    // Check if it's an ISO string (contains 'T' or 'Z')
    if (dateString.includes('T') || dateString.includes('Z')) {
      // Old format: ISO string - extract just the date part
      const datePart = dateString.split('T')[0];
      date = new Date(datePart + 'T00:00:00');
    } else if (dateString.includes('GMT') || dateString.includes('UTC')) {
      // Handle full JavaScript Date string format (e.g., "Sun May 24 2026 05:30:00 GMT+0530 (India Standard Time)")
      // Parse the full date string and extract just the date components
      const parsedDate = new Date(dateString);
      if (!isNaN(parsedDate.getTime())) {
        // Extract date components to avoid timezone issues
        const year = parsedDate.getFullYear();
        const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
        const day = String(parsedDate.getDate()).padStart(2, '0');
        date = new Date(`${year}-${month}-${day}T00:00:00`);
      } else {
        throw new Error('Invalid full date string');
      }
    } else if (dateString.includes(' ')) {
      // Handle date strings with time (e.g., "2026-07-18 21:00") - extract just the date part
      const datePart = dateString.split(' ')[0];
      date = new Date(datePart + 'T00:00:00');
    } else {
      // New format: YYYY-MM-DD string
      date = new Date(dateString + 'T00:00:00');
    }
    
    // Validate that the date is valid
    if (isNaN(date.getTime())) {
      console.warn("Invalid date created from string:", dateString);
      return undefined;
    }
    
    return date;
  } catch (error) {
    console.warn("Error creating date for calendar:", error, "Input:", dateString);
    return undefined;
  }
}

/**
 * Converts an old ISO date string to the new consistent format
 * @param isoDateString - The old ISO date string
 * @returns New consistent date string (YYYY-MM-DD)
 */
export function migrateOldDateString(isoDateString: string | null): string | null {
  if (!isoDateString) return null;
  
  try {
    // Check if it's already in the new format
    if (!isoDateString.includes('T') && !isoDateString.includes('Z') && !isoDateString.includes(' ') && !isoDateString.includes('GMT') && !isoDateString.includes('UTC')) {
      return isoDateString; // Already in new format
    }
    
    // Extract date part from various formats
    let datePart: string;
    if (isoDateString.includes('T')) {
      datePart = isoDateString.split('T')[0];
    } else if (isoDateString.includes('GMT') || isoDateString.includes('UTC')) {
      // Handle full JavaScript Date string format
      const parsedDate = new Date(isoDateString);
      if (!isNaN(parsedDate.getTime())) {
        const year = parsedDate.getFullYear();
        const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
        const day = String(parsedDate.getDate()).padStart(2, '0');
        datePart = `${year}-${month}-${day}`;
      } else {
        throw new Error('Invalid full date string');
      }
    } else if (isoDateString.includes(' ')) {
      datePart = isoDateString.split(' ')[0];
    } else {
      datePart = isoDateString;
    }
    
    return datePart;
  } catch (error) {
    console.warn("Error migrating old date string:", error);
    return null;
  }
}

/**
 * Normalizes a date string to YYYY-MM-DD format, removing any time component
 * @param dateString - The date string that may include time (e.g., "2026-07-18 21:00")
 * @returns Normalized date string in YYYY-MM-DD format
 */
export function normalizeDateString(dateString: string | null): string | null {
  if (!dateString) return null;
  
  try {
    // Check if it's already in the correct format
    if (!dateString.includes('T') && !dateString.includes('Z') && !dateString.includes(' ') && !dateString.includes('GMT') && !dateString.includes('UTC')) {
      return dateString; // Already in YYYY-MM-DD format
    }
    
    // Extract date part from various formats
    let datePart: string;
    if (dateString.includes('T')) {
      datePart = dateString.split('T')[0];
    } else if (dateString.includes('GMT') || dateString.includes('UTC')) {
      // Handle full JavaScript Date string format
      const parsedDate = new Date(dateString);
      if (!isNaN(parsedDate.getTime())) {
        const year = parsedDate.getFullYear();
        const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
        const day = String(parsedDate.getDate()).padStart(2, '0');
        datePart = `${year}-${month}-${day}`;
      } else {
        throw new Error('Invalid full date string');
      }
    } else if (dateString.includes(' ')) {
      datePart = dateString.split(' ')[0];
    } else {
      datePart = dateString;
    }
    
    return datePart;
  } catch (error) {
    console.warn("Error normalizing date string:", error);
    return null;
  }
}

/**
 * Validates if a date string is valid and can be safely used
 * @param dateString - The date string to validate
 * @returns true if the date string is valid, false otherwise
 */
export function isValidDateString(dateString: string | null): boolean {
  if (!dateString) return false;
  
  try {
    const date = createDateForCalendar(dateString);
    return date !== undefined && !isNaN(date.getTime());
  } catch (error) {
    return false;
  }
}

/**
 * Safely creates a date for calendar display with fallback handling
 * @param dateString - The date string to convert
 * @param fallbackDate - Optional fallback date if the input is invalid
 * @returns Date object for calendar display or undefined if invalid
 */
export function safeCreateDateForCalendar(
  dateString: string | null, 
  fallbackDate?: Date
): Date | undefined {
  const date = createDateForCalendar(dateString);
  
  if (date === undefined && fallbackDate) {
    return fallbackDate;
  }
  
  return date;
}

/**
 * Parses a date + time as wall-clock components (timezone-neutral).
 * Use with fromZonedTime to interpret the result in a specific IANA timezone.
 */
export function parseLocalDateTime(
  dateString: string,
  timeString: string
): Date | null {
  if (!dateString || !timeString) return null;

  try {
    const [year, month, day] = dateString.split("-").map(Number);
    const [hours, minutes] = timeString.split(":").map(Number);
    if ([year, month, day, hours, minutes].some((n) => isNaN(n))) return null;

    const date = new Date(year, month - 1, day, hours, minutes);
    return isNaN(date.getTime()) ? null : date;
  } catch {
    return null;
  }
}

/**
 * Returns the number of calendar days between two YYYY-MM-DD date strings.
 */
export function daysBetweenDateStrings(
  startDate: string,
  endDate: string
): number {
  const start = createDateForCalendar(startDate);
  const end = createDateForCalendar(endDate);
  if (!start || !end) return 0;
  return Math.ceil((end.getTime() - start.getTime()) / (1000 * 3600 * 24));
}

/**
 * Gets the user's timezone from browser or defaults to UTC
 * @returns User's timezone string
 */
export function getUserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (error) {
    console.warn("Could not detect user timezone, defaulting to UTC:", error);
    return "UTC";
  }
}
